import useHttp from '../helpers/HttpMethods';

const http = useHttp();

class InvoiceService {
  /**
   * Lista as faturas paginadas.
   * @param {string} filter - Filtros em string (page, size, status, customerId).
   * @returns {Promise<any>} - Faturas em formato paginado.
   */
  static async list(filter: string): Promise<any> {
    return await http.get(`/invoices?${filter}`);
  }

  /**
   * Busca uma fatura por ID.
   * @param {number} invoiceId - ID da fatura.
   * @returns {Promise<any>} - Dados da fatura.
   */
  static async findById(invoiceId: number): Promise<any> {
    return await http.get(`/invoices/${invoiceId}`);
  }

  /**
   * Cria uma nova fatura.
   * @param {any} body - Dados da fatura (billableItemIds).
   * @returns {Promise<any>} - Fatura criada.
   */
  static async create(body: any): Promise<any> {
    return await http.post(body, '/invoices');
  }

  /**
   * Atualiza um item da fatura.
   * @param {number} invoiceId - ID da fatura.
   * @param {number} itemId - ID do item da fatura.
   * @param {any} body - Dados atualizados (quantity, unitPrice).
   * @returns {Promise<any>} - Item atualizado.
   */
  static async updateItem(
    invoiceId: number,
    itemId: number,
    body: any
  ): Promise<any> {
    return await http.put(body, `/invoices/${invoiceId}/items/${itemId}`);
  }

  /**
   * Aprova uma fatura.
   * @param {number} invoiceId - ID da fatura.
   * @returns {Promise<any>} - Resposta da API.
   */
  static async approve(invoiceId: number): Promise<any> {
    return await http.patch({}, `/invoices/${invoiceId}/approve`);
  }

  /**
   * Marca uma fatura como concluída.
   * @param {number} invoiceId - ID da fatura.
   * @returns {Promise<any>} - Resposta da API.
   */
  static async markAsCompleted(invoiceId: number): Promise<any> {
    return await http.patch({}, `/invoices/${invoiceId}/complete`);
  }

  /**
   * Exporta uma fatura em PDF.
   * @param {number} invoiceId - ID da fatura.
   * @returns {Promise<Response>} - Arquivo PDF.
   */
  static async exportPdf(invoiceId: number): Promise<Response> {
    return await http.getPdf(`/invoices/${invoiceId}/export/pdf`);
  }

  /**
   * Adiciona um item à fatura.
   * @param {number} invoiceId - ID da fatura.
   * @param {any} body - Dados do item (productServiceId, quantity, unitPrice).
   * @returns {Promise<any>} - Item adicionado.
   */
  static async addItem(invoiceId: number, body: any): Promise<any> {
    return await http.post(body, `/invoices/${invoiceId}/items`);
  }

  /**
   * Remove um item da fatura.
   * @param {number} invoiceId - ID da fatura.
   * @param {number} itemId - ID do item da fatura.
   * @returns {Promise<any>} - Resposta da API.
   */
  static async deleteItem(invoiceId: number, itemId: number): Promise<any> {
    return await http.del(`/invoices/${invoiceId}/items/${itemId}`);
  }

  /**
   * Atualiza o status da fatura.
   * @param {number} invoiceId - ID da fatura.
   * @param {string} newStatus - Novo status (APPROVED, COMPLETED, CANCELED).
   * @param {boolean} [returnItemsToPending] - Se verdadeiro, retorna os itens para pendente ao cancelar.
   * @returns {Promise<any>} - Resposta da API.
   */
  static async updateStatus(
    invoiceId: number,
    newStatus: string,
    returnItemsToPending?: boolean
  ): Promise<any> {
    const body: any = { newStatus };
    if (returnItemsToPending !== undefined) {
      body.returnItemsToPending = returnItemsToPending;
    }
    return await http.patch(body, `/invoices/${invoiceId}/status`);
  }

  /**
   * Exporta múltiplas faturas.
   * @param {number[]} invoiceIds - Lista de IDs das faturas a serem exportadas.
   * @param {string} fileType - Tipo de arquivo: "pdf" ou "excel" (xlsx). Padrão: "pdf".
   * @param {string} exportType - Tipo de exportação: "summary" ou "detailed". Padrão: "summary".
   * @returns {Promise<Response>} - Arquivo exportado.
   */
  static async export(
    invoiceIds: number[],
    fileType: string = 'pdf',
    exportType: string = 'summary'
  ): Promise<Response> {
    const body = {
      invoiceIds,
      fileType,
      exportType
    };
    return await http.postExport(body, '/invoices/export');
  }
}

export default InvoiceService;
